import React, { useState, useEffect, useCallback, useMemo } from "react";
import { colors, font } from "../utils/theme.js";
import { section, inputStyle, primaryBtn, ghostBtn, linkBtn } from "../clients/ui.js";
import { todayOf } from "../clients/clientLogic.js";
import { getApiToken } from "../auth/apiToken.js";
import { intakeApi } from "./api.js";
import { notificationRecipients } from "./recipients.js";

const STATUS_LABEL = {
  sent: "Sent", opened: "Opened (approx.)", expired: "Expired", revoked: "Revoked",
  processing: "Processing", complete: "Complete", failed: "Processing failed",
};
const fmt = (iso) => (iso ? String(iso).slice(0, 10) : "—");

// Intake tab on the client page. The invite link is shown once, kept only in component state.
export default function IntakeTab({ clientId, client, projects, users, flash }) {
  const [phase, setPhase] = useState("loading"); // loading | signin | error | ready
  const [invites, setInvites] = useState([]);
  const [link, setLink] = useState(null);
  const [contact, setContact] = useState(client?.pocs?.[0]?.email || "");
  const [sending, setSending] = useState(false);
  const recipients = useMemo(() => notificationRecipients({ projects, users, today: todayOf() }), [projects, users]);

  const [refreshFailed, setRefreshFailed] = useState(false);

  // quiet = a refresh after sending: never leave the ready phase, so the one-time link stays on screen.
  const load = useCallback(async (quiet = false) => {
    const fail = (p) => { if (quiet) setRefreshFailed(true); else setPhase(p); };
    const token = await getApiToken();
    if (!token) { fail("signin"); return; }
    const r = await intakeApi.clientStatus(clientId);
    if (r.status === 401) { fail("signin"); return; }
    if (!r.ok) { fail("error"); return; }
    setInvites(r.data?.invites || []);
    setRefreshFailed(false);
    setPhase("ready");
  }, [clientId]);
  useEffect(() => { setPhase("loading"); setLink(null); load(); }, [load]);

  const send = async () => {
    setSending(true);
    const r = await intakeApi.createInvite({ clientId, clientDisplayName: client.name, contactEmail: contact.trim(), notifyEmails: recipients });
    setSending(false);
    if (r.ok) { setLink(r.data?.link || null); load(true); return; }
    if (r.status === 409) flash("error", "This client already has an open invite. Use Reissue.");
    else if (r.status === 400) flash("error", "Check the contact email.");
    else flash("error", "Couldn't send onboarding. Try again.");
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); flash("success", "Link copied"); }
    catch (_) { flash("error", "Couldn't copy. Select the link and copy it."); }
  };

  if (phase === "loading") return <div style={{ color: colors.textMuted, fontSize: 13 }}>Loading intake…</div>;
  if (phase === "signin") return <div style={{ color: colors.textMuted, fontSize: 13 }}>Sign in again to use intake.</div>;
  if (phase === "error") return (
    <div style={{ color: colors.textMuted, fontSize: 13 }}>
      Couldn't load intake. Try again. <button style={linkBtn} onClick={() => { setPhase("loading"); load(); }}>Retry</button>
    </div>
  );

  const hasOpen = invites.some((i) => i.status === "sent" || i.status === "opened");
  return (
    <div style={{ fontFamily: font }}>
      {link && (
        <div style={section}>
          <div style={{ fontSize: 13, color: colors.text, marginBottom: 8 }}>Client link</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input readOnly value={link} onFocus={(e) => e.target.select()} style={{ ...inputStyle, flex: 1 }} />
            <button style={primaryBtn} onClick={copy}>Copy</button>
          </div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>This link is shown once. Copy it into your email now.</div>
        </div>
      )}
      {refreshFailed && (
        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>
          Couldn't refresh the list. <button style={linkBtn} onClick={() => load(true)}>Retry</button>
        </div>
      )}
      {!hasOpen && !link && (
        <div style={section}>
          <div style={{ fontSize: 13, color: colors.text, marginBottom: 8 }}>Contact email</div>
          <input type="email" value={contact} onChange={(e) => setContact(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
          <div style={{ fontSize: 12, color: colors.textMuted, margin: "8px 0 12px" }}>Will notify: {recipients.join(", ")}</div>
          <button style={{ ...primaryBtn, opacity: sending ? 0.6 : 1 }} disabled={sending} onClick={send}>Send onboarding</button>
        </div>
      )}
      <div style={section}>
        <div style={{ fontSize: 13, color: colors.text, marginBottom: 8 }}>Invites</div>
        {invites.length === 0 && <div style={{ fontSize: 13, color: colors.textMuted }}>No invites yet.</div>}
        {invites.map((i) => (
          <div key={i.inviteId} style={{ display: "flex", gap: 16, fontSize: 13, padding: "6px 0", borderTop: `1px solid ${colors.border}` }}>
            <span style={{ color: colors.textMuted, minWidth: 90 }}>{fmt(i.createdAt)}</span>
            <span style={{ color: colors.text }}>{STATUS_LABEL[i.status] || i.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
